import { Button, Card, FooterAmount } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import TotalCard from 'components/molecules/card/total/TotalCard';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';

import { QueryFilter, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';

import { useTranslation } from 'react-i18next';
import { PiDownload, PiInvoice } from 'react-icons/pi';

import {
  useGetSettlementFile,
  useGetSettlementPeriodsLightList,
  useGetSettlementSettlementsInfinite,
} from 'services/settlement';
import { ICreateSettlementOrder, IStatementsSearch } from 'services/settlement/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

import { formatCurrency } from 'utils/global/currency.utils';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import LabeledSettlementOrderStatus from 'components/molecules/labeledStatus/LabelSettlementOrderStatus';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { useStoreState } from 'providers/StoreStateContext';
import { useGetSettlementOrders, usePostSettlementOrder } from 'services/settlement/order';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, string } from 'yup';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { useEffect } from 'react';
import { registerSettlementQueryFilters } from 'queryFilters/settlementQueryFilter';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  net,
  settlementNumber,
}

export interface Provider {
  id: string;
  name: string;
}
const statementsSearchInitialValues: IStatementsSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};
const Settlements = () => {
  const columnsVisibility = useTableColumnsVisibility('SettlementList1', SORT_COLUMNS);

  const settlementPeriods = useGetSettlementPeriodsLightList();

  const requestSettlement = usePostSettlementOrder();

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[columnsVisibility.tableId] || statementsSearchInitialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage, statistics } =
    useGetSettlementSettlementsInfinite(filters);

  const settlementOrders = useGetSettlementOrders();

  const { setFile } = useStreamFileDownload(useGetSettlementFile);
  const scroll = useScroll({ fetchNextPage: fetchNextPage });
  const { t } = useTranslation();

  const i18nextPrefix = 'settlements';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const settlementPeriodOptions = settlementPeriods.data?.map((s) => ({
    label: getSettlementPeriodName(s.settlementPeriodName),
    value: s.id,
  }));

  const getSettlementPeriodNameById = (id: string) =>
    getSettlementPeriodName(settlementPeriods.data?.find((s) => s.id === id)?.settlementPeriodName);

  const items = data?.pages?.flat() ?? [];
  const ordersLen = settlementOrders.data?.length ?? 0;

  const hideList = items.length < 1 && ordersLen > 0;

  const { isBanned } = useIsUserBanned();

  useEffect(() => {
    if (!settlementPeriodOptions?.length) return;

    const validOptions = settlementPeriodOptions.flatMap((o) =>
      o.label ? [{ value: o.value, label: o.label }] : [],
    );

    registerSettlementQueryFilters(validOptions);
  }, [settlementPeriodOptions]);
  return (
    <TopbarPage
      topbarAbsolute
      footer={
        <TotalCard
          header={t('table.total')}
          content={<FooterAmount statistic={statistics?.allNetAmount} />}
        />
      }
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=""
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex gap-2">
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                {!isBanned && (
                  <PopupForm<ICreateSettlementOrder>
                    header={
                      <>
                        <PiInvoice /> {t('button.settlement-order')}
                      </>
                    }
                    initialValues={{
                      settlementPeriodId: '',
                    }}
                    validationSchema={object().shape({
                      settlementPeriodId: string().required(t('formik-error.required')),
                    })}
                    submitButtonText={t('button.send-request')}
                    onSubmit={async (values, helpers, actions) =>
                      await requestSettlement.mutateAsync(values, {
                        onError: (error) => onErrorShowAllErrorsToast(error),
                        onSuccess: () => {
                          settlementOrders.refetch();
                          helpers.resetForm();
                          actions?.close();
                        },
                      })
                    }
                    trigger={
                      <Button onlyIcon variant={BUTTON_VARIANT.submit} disabled={ordersLen > 0}>
                        <Button.Icon icon={<PiInvoice />} />
                        <Button.Description
                          description={
                            ordersLen > 0
                              ? t('button.settlement-order-disabled')
                              : t('button.settlement-order')
                          }
                        />
                      </Button>
                    }
                  >
                    {' '}
                    <FormInput.Select
                      appBackground
                      name="settlementPeriodId"
                      label={t('input.label.settlementPeriod')}
                      options={settlementPeriodOptions}
                    />
                    <div />
                    <div />
                  </PopupForm>
                )}
              </div>
            </>
          }
        >
          <QueryFilter<IStatementsSearch> i18nextPrefix={i18nextPrefix} initialValues={filters}>
            <>
              <QueryFilter.Form<IStatementsSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(columnsVisibility.tableId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="startSettlementPeriodId"
                    label={t('input.label.startSettlementPeriod')}
                    placeholder=""
                    options={settlementPeriodOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="endSettlementPeriodId"
                    placeholder=""
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="settlement" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      {!!settlementOrders.data?.length && (
        <ListWrapper
          view="list"
          items={settlementOrders.data}
          style={{ paddingBottom: 20 }}
          item={(settlement) => (
            <Card
              view="list"
              key={settlement.id}
              header={
                settlement.startSettlementPeriodId === settlement.endSettlementPeriodId
                  ? getSettlementPeriodNameById(settlement.startSettlementPeriodId)
                  : `${getSettlementPeriodNameById(
                      settlement.startSettlementPeriodId,
                    )} - ${getSettlementPeriodNameById(settlement.endSettlementPeriodId)}`
              }
              description={t('button.settlement-order')}
              status={<LabeledSettlementOrderStatus status={settlement.settlementOrderStatusId} />}
            />
          )}
        />
      )}
      {!hideList && (
        <ListWrapper
          view="list"
          style={{ paddingBottom: 100 }}
          items={data?.pages.flat()}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          item={(settlement) => (
            <Card
              view="list"
              key={settlement.id}
              header={
                settlement.startSettlementPeriodId === settlement.endSettlementPeriodId
                  ? getSettlementPeriodName(settlement.startSettlementPeriodName)
                  : `${getSettlementPeriodName(
                      settlement.startSettlementPeriodName,
                    )} - ${getSettlementPeriodName(settlement.endSettlementPeriodName)}`
              }
              description={settlement.settlementNumber}
              status={
                <div className="flex w-max font-bold items-center gap-4" style={{ fontSize: 18 }}>
                  {formatCurrency(settlement.netAmount)}

                  <MagellanPopups.FilePreviewPopup
                    fileId={settlement.id}
                    fileName={settlement.settlementNumber ?? ''}
                    fileService={useGetSettlementFile}
                    trigger={
                      <Button type="button" isClear onlyIcon disabled={!settlement.filePath}>
                        <Button.Icon icon={<PiDownload />} />
                      </Button>
                    }
                    onDownload={() =>
                      setFile({
                        id: settlement.id,
                        name: settlement.settlementNumber,
                      })
                    }
                  />
                </div>
              }
            />
          )}
        />
      )}
    </TopbarPage>
  );
};

export default Settlements;
